import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { fetchBotResponse, fetchChunkReference } from '../../services/api/KaiConfigData';
import { FaRobot as FaRobotIcon  } from "react-icons/fa";

import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";
import { getSafeImageSrc } from '../../utils/imageUtils';

// Import the split components
import MessageList from '../v1/MessageList';
import ChatInput from '../v1/ChatInput';
import ReferenceModal from '../ReferenceModal';
import { Message, ReferenceItem, ChatProps } from '../../types/types';

declare global {
  interface Window {
    MathJax?: {
      typeset?: (elements?: Array<HTMLElement>) => void;
      typesetPromise?: (elements?: Array<HTMLElement>) => Promise<any>;
      startup?: {
        promise?: Promise<any>;
      };
      tex?: any;
      svg?: any;
      options?: any;
      [key: string]: any;
    }
  }
}

const Chat: React.FC<ChatProps & {themeOverride?: 'light' | 'dark' | 'auto'}> = ({ 
  configData, 
  messages, 
  activeChatId, 
  onSendMessage, 
  isSidebarOpen, 
  pendingMsgCount, 
  allowSendMsg,
  themeOverride = 'auto'
}) => {
  const [input, setInput] = useState("");
  const [isMobileState, setIsMobileState] = useState(false);
  const { app_id } = useParams(); 
  const [netId, setNetId] = useState(localStorage.getItem('net_id'));
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<Map<string, Message[]>>(new Map());
  
  // Ref to the last message
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [isNewChat, setIsNewChat] = useState(false);
  const [typingChatId, setTypingChatId] = useState<string | null>(null);
  const scrollButtonRef = useRef<HTMLButtonElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);

  // Reference modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const [reference, setReference] = useState<ReferenceItem[]>([]);

  const [hasTyped, setHasTyped] = useState(false);
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(configData.app_ai_settings.ai_face);
  const [showScrollButton, setShowScrollButton] = useState<Map<string, boolean>>(new Map());

  const loadingIndicatorRef = useRef<HTMLDivElement | null>(null);

  const app_ai_settings = configData && configData?.app_ai_settings;

  // Update safe source when app_ai_face changes
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = configData.app_ai_settings.ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };
    updateSafeSource();
  }, [configData.app_ai_settings.ai_face]);

  // Handle chunk reference
  const handleChunkReference = async (chatid: string) => {
    setIsModalOpen(true);
    setIsLoadingChunks(true);
    setReference([]);

    try {
      const result = await fetchChunkReference(chatid);
      const chatReferences = result?.data?.chat_references || result?.data || [];
      
      if (!chatReferences || chatReferences.length === 0) {
        setIsLoadingChunks(false);
        return;
      }

      const allChunkItems: ReferenceItem[] = chatReferences.map((referenceData: any, index: number) => {
        const meta = referenceData.chunk_metadata || referenceData.highest_scoring_chunk?.metadata || referenceData;
        
        return {
          chunk: meta.text || referenceData.highest_scoring_chunk?.text || '',
          file_name: meta.source_object_name || '',
          show: false,
          course_name: meta.source_parent_name || '',
          url: meta.source_object_url || '',
          source: meta.source || referenceData.source || '',
          source_category: meta.source_category || '',
        };
      }).filter((item: ReferenceItem) => item.chunk.trim() !== '');

      setReference(allChunkItems);
    } catch (error) {
      console.error('Error fetching chunk references:', error);
    } finally {
      setIsLoadingChunks(false);
    }
  };

  const toggleChunk = (index: number) => {
    setReference(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, show: !item.show } : item
      )
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  // Get bot response
  const getBotResponse = async (message: string) => {
    const result = await fetchBotResponse(app_id || '', netId || '', message, activeChatId); 
    const botResponse = `${result.data.response}`;
    return result;
  };

  // Update messages when new messages come in
  useEffect(() => {
    setChatMessages((prev) => {
      const updatedMessages = new Map(prev);
      updatedMessages.set(activeChatId, messages);
      return updatedMessages;
    });
  }, [messages, activeChatId]);

  // Force scroll state refresh when switching chats
  useEffect(() => {
    if (activeChatId) {
      // Clear any existing scroll state for this chat to force re-evaluation
      setShowScrollButton((prev) => {
        const updated = new Map(prev);
        updated.delete(activeChatId); // Remove existing state
        return updated;
      });
      
      // Re-evaluate scroll state after a short delay
      setTimeout(() => {
        if (chatContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
          const scrollBottom = scrollTop + clientHeight;
          const atBottom = Math.abs(scrollHeight - scrollBottom) <= 10;
          const hasScrollableContent = scrollHeight > clientHeight + 50;
          
          setShowScrollButton((prev) => {
            const updated = new Map(prev);
            updated.set(activeChatId, hasScrollableContent && !atBottom);
            return updated;
          });
        }
      }, 200);
    }
  }, [activeChatId]);

  useEffect(() => {
    if(typingChatId === 'chat'){
      setTypingChatId(activeChatId)
    }
  }, [typingChatId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileState(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const scrollBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    if (scrollButtonRef.current) {
      scrollButtonRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const sendMessage = async (message: string) => {
    setHasTyped(false)
    if (!message.trim()) return;
    setIsNewChat(true);
    
    const newMessage: Message = { sender: 'user', message: message };
    setInput("");
    scrollToBottom();
    
    setChatMessages((prev) => {
      const updatedMessages = new Map(prev);
      const currentMessages = updatedMessages.get(activeChatId) || [];
      updatedMessages.set(activeChatId, [...currentMessages, newMessage]);
      return updatedMessages;
    });
  
    let chatIdFromResponse = '';
    onSendMessage(newMessage, activeChatId, chatIdFromResponse);
  
    setLoading(true);
    // Immediate scroll when loading starts
    setTimeout(() => {
      scrollLoadingBottom();
    }, 50);
    
    try {
      let botResponse = await getBotResponse(message);
      const isRefAvailable = botResponse?.data.is_ref_available ?? false;
      const message_id = botResponse?.data.message_id ?? '';
      
      const botMessage: Message = { 
        sender: 'assistant', 
        message: botResponse?.data.response, 
        isRefAvailable,
        message_id: message_id
      };
      chatIdFromResponse = botResponse?.data.chat_id;
      
      let botMsg = botResponse?.data.response;
      
      setTypingChatId(activeChatId); 
      onSendMessage(botMessage, activeChatId, chatIdFromResponse);
    
      setChatMessages((prev) => {
        const updatedMessages = new Map(prev);
        const currentMessages = updatedMessages.get(activeChatId) || [];
        updatedMessages.set(activeChatId, [...currentMessages, botMessage]);
        return updatedMessages;
      });
      
      setTimeout(() => {
        scrollBottom();
      }, 100);
      
      setInput("");
      setTimeout(() => {
        setIsNewChat(false);
      }, botMsg.length * 50); 
    } catch (error) {
      console.error("Error fetching bot response:", error);
    } finally {
      setLoading(false);
      setInput("");
    }
  };

  // Scroll management
  useEffect(() => {
    const handleScroll = () => {
      if (chatContainerRef.current && activeChatId) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const scrollBottom = scrollTop + clientHeight;
        const atBottom = Math.abs(scrollHeight - scrollBottom) <= 10; // Increased tolerance
        const hasScrollableContent = scrollHeight > clientHeight + 50;
        
        setShowScrollButton((prev) => {
          const updated = new Map(prev);
          updated.set(activeChatId, hasScrollableContent && !atBottom);
          return updated;
        });
      }
    };

    // Enhanced scroll handler with immediate and delayed checks
    const enhancedScrollHandler = () => {
      handleScroll(); // Immediate check
      
      // Additional check after a short delay to catch smooth scrolling
      setTimeout(handleScroll, 50);
      setTimeout(handleScroll, 200);
    };

    // Store the handler reference for cleanup
    scrollHandlerRef.current = enhancedScrollHandler;

    const chatDiv = chatContainerRef.current;
    if (chatDiv && activeChatId) {      
      chatDiv.addEventListener("scroll", enhancedScrollHandler);
      chatDiv.addEventListener("scrollend", handleScroll);
      
      // Multiple checks to ensure proper initialization
      setTimeout(handleScroll, 100);
      setTimeout(handleScroll, 300);
      setTimeout(handleScroll, 500);
    }

    return () => {
      if (chatDiv && scrollHandlerRef.current) {
        chatDiv.removeEventListener("scroll", scrollHandlerRef.current);
        chatDiv.removeEventListener("scrollend", handleScroll);
        scrollHandlerRef.current = null;
      }
    };
  }, [activeChatId, messages.length]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      
      // Force update scroll button state after scroll completes
      setTimeout(() => {
        if (chatContainerRef.current && activeChatId) {
          const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
          const scrollBottom = scrollTop + clientHeight;
          const atBottom = Math.abs(scrollHeight - scrollBottom) <= 10;
          const hasScrollableContent = scrollHeight > clientHeight + 50;
          
          setShowScrollButton((prev) => {
            const updated = new Map(prev);
            updated.set(activeChatId, hasScrollableContent && !atBottom);
            return updated;
          });
        }
      }, 100); // Check after smooth scroll animation
      
      // Additional check after longer delay for smooth scroll completion
      setTimeout(() => {
        if (chatContainerRef.current && activeChatId) {
          const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
          const scrollBottom = scrollTop + clientHeight;
          const atBottom = Math.abs(scrollHeight - scrollBottom) <= 10;
          
          setShowScrollButton((prev) => {
            const updated = new Map(prev);
            updated.set(activeChatId, false); // Force hide when scrollToBottom is called
            return updated;
          });
        }
      }, 800); // After smooth scroll animation completes
    }
    
    if (scrollButtonRef.current) {
      scrollButtonRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const handleScrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  useEffect(() => {
    handleScrollToBottom();
    // Trigger scroll detection after content changes - multiple checks for reliability
    const timeouts = [100, 300, 600]; // Multiple timing checks
    
    timeouts.forEach(delay => {
      setTimeout(() => {
        if (chatContainerRef.current && activeChatId) {
          const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
          const scrollBottom = scrollTop + clientHeight;
          const atBottom = Math.abs(scrollHeight - scrollBottom) <= 10; // Consistent tolerance
          const hasScrollableContent = scrollHeight > clientHeight + 50;
          
          setShowScrollButton((prev) => {
            const updated = new Map(prev);
            updated.set(activeChatId, hasScrollableContent && !atBottom);
            return updated;
          });
        }
      }, delay);
    });
  }, [chatMessages, activeChatId]);

  // Add animation styles for scroll button
  useEffect(() => {
    const styleId = 'v1-scroll-button-styles';
    let existingStyle = document.getElementById(styleId);
    
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
      existingStyle = style;
    }
    
    existingStyle.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translate(-50%, 10px);
        }
        to {
          opacity: 1;
          transform: translate(-50%, 0);
        }
      }
      
      .animate-fade-in {
        animation: fadeInUp 0.2s ease-out forwards;
      }
    `;
  }, []);

  useEffect(() => {
    let scrollInterval: any;
    
    if (loading) {
      // Immediate scroll when loading starts
      setTimeout(() => {
        scrollLoadingBottom();
      }, 100);
      
      // Continue scrolling periodically during loading
      scrollInterval = setInterval(() => {
        scrollLoadingBottom();
      }, 300);
    }
    
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [loading]);

  const scrollLoadingBottom = () => {
    if (loading && loadingIndicatorRef.current) {
      loadingIndicatorRef.current.scrollIntoView({ behavior: 'smooth', block: "end" });
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: "end" });
    }
    
    // Also ensure the chat container scrolls to bottom
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [chatMessages]);

  // Use configData colors as default with theme override
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const configIsLight = isLightColor(backgroundColor);
  
  // Determine effective theme based on themeOverride
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';
  
  // Use theme-aware colors
  const chatBackground = themeOverride === 'auto' 
    ? backgroundColor || (configIsLight ? '#ffffff' : '#212121')
    : (effectiveIsLight ? '#ffffff' : '#212121');
    
  const themeTextColor = themeOverride === 'auto'
    ? configData.app_ui_settings.ui_fontcolor
    : (effectiveIsLight ? '#000000' : '#ffffff');
    
  const lighterInputBoxBackground = themeOverride === 'auto'
    ? (configIsLight ? lightenColor(backgroundColor, 5) : darkenColor(backgroundColor, 10))
    : (effectiveIsLight ? '#f7f7f8' : '#2d2d30');
    
  const activeMessages = chatMessages.get(activeChatId) || [];
  const maxHeight = isMobileState ? 'calc(100vh - 180px)' : 'calc(100vh - 120px)';

  return (
    <div className="flex h-screen w-full">
      <div 
        className="flex-1 flex flex-col transition-all duration-300" 
        style={{ 
          backgroundColor: chatBackground,
          marginLeft: isSidebarOpen && !isMobileState ? '256px' : (!isMobileState ? '52px' : '0') // Collapsed sidebar only on desktop
        }}
      >
        
        {/* When no messages - Center everything with ChatGPT style */}
        {activeMessages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-4 w-full max-w-6xl mx-auto">
            {/* AI Icon - larger and more prominent like ChatGPT */}
            <div className="mb-8">
              {safeAiIconSrc ? (
                 <img
                  src={safeAiIconSrc}
                  alt="AI Face"
                  className="h-10 w-10 sm:h-14 sm:w-14 md:h-18 md:w-18"
                  style={{ color: configData.app_ui_settings.ui_iconcolor }}
                />
              ) : (
                <div className="h-10 w-10 sm:h-20 sm:w-20 flex items-center justify-center"
                     style={{ backgroundColor: effectiveIsLight ? '#f7f7f8' : '#2d2d30' }}>
<FaRobotIcon
                   className="h-10 w-10 sm:h-14 sm:w-14 md:h-18 md:w-18"
                   style={{ color: themeTextColor }}
                 />
                </div>
              )}
            </div>

            {/* Greeting - ChatGPT style */}
            {String(app_ai_settings.ai_showgreeting) === 'true' && (
              <div className="text-center mb-12 max-w-3xl">
                <h1
                  className={`text-lg sm:text-xl md:text-xl text-center mb-8 max-w-2xl ${
                    app_ai_settings.ai_greeting && app_ai_settings.ai_greeting.length > 105 ? 'text-xl sm:text-2xl md:text-3xl' : ''
                  }`}
                  style={{ color: themeTextColor }}
                >
                  {app_ai_settings.ai_greeting
                    ? app_ai_settings.ai_greeting
                        .replace(/undefined/g, '')
                        .replace(/\s+/g, ' ')
                        .trim()
                    : 'How can I help you today?'
                  }
                </h1>
              </div>
            )}

            {/* Chat Input - Centered */}
            <ChatInput
              input={input}
              setInput={setInput}
              onSendMessage={sendMessage}
              configData={configData}
              loading={loading}
              pendingMsgCount={pendingMsgCount}
              allowSendMsg={allowSendMsg}
              messages={activeMessages}
              activeChatId={activeChatId}
              isSidebarOpen={isSidebarOpen}
              themeOverride={themeOverride}
            />
          </div>
        ) : (
          /* When messages exist - Normal layout */
          <>
            {/* Main Content - ChatGPT style */}
            <div
              id="previewContent"
              className="flex-1 overflow-y-auto w-full h-full"
              ref={chatContainerRef}
              style={{
                backgroundColor: chatBackground,
                color: themeTextColor,
                maxHeight: maxHeight,
                scrollbarGutter: 'stable both-edges',
                paddingBottom: '80px', // Further reduced space for text input
              }}
            >
              <div className="max-w-4xl mx-auto px-4 pt-6 pb-1">
                <MessageList
                  messages={activeMessages}
                  configData={configData}
                  activeChatId={activeChatId}
                  isNewChat={isNewChat}
                  typingChatId={typingChatId}
                  hasTyped={hasTyped}
                  loading={loading}
                  isSidebarOpen={isSidebarOpen}
                  onScrollBottom={scrollBottom}
                  onChunkReference={handleChunkReference}
                  messagesEndRef={messagesEndRef}
                  loadingIndicatorRef={loadingIndicatorRef}
                  safeAiIconSrc={safeAiIconSrc}
                  themeOverride={themeOverride}
                />
              </div>

              {/* Scroll Down Button - ChatGPT style */}
              {activeMessages.length > 0 && showScrollButton.get(activeChatId) && (
                <div 
                  className="fixed bottom-24 z-50 animate-fade-in"
                  style={{
                    left: isSidebarOpen && !isMobileState ? 'calc(50% + 128px)' : (!isMobileState ? 'calc(50% + 26px)' : '50%'), // Center to chat area
                    transform: 'translateX(-50%)'
                  }}
                >
                  <button
                    ref={scrollButtonRef}
                    onClick={scrollToBottom}
                    className="group relative flex items-center justify-center w-10 h-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
                    style={{ 
                      backgroundColor: effectiveIsLight ? '#ffffff' : '#2d2d30',
                      border: `1px solid ${effectiveIsLight ? '#d1d5db' : '#4b5563'}`,
                      boxShadow: effectiveIsLight 
                        ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        : '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    <ChevronDown 
                      size={18} 
                      style={{ 
                        color: effectiveIsLight ? '#374151' : '#d1d5db'
                      }} 
                    />
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <div 
                        className="px-2 py-1 text-xs font-medium rounded shadow-lg whitespace-nowrap"
                        style={{
                          backgroundColor: effectiveIsLight ? '#1f2937' : '#f9fafb',
                          color: effectiveIsLight ? '#ffffff' : '#1f2937'
                        }}
                      >
                        Scroll to bottom
                        <div 
                          className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0"
                          style={{
                            borderLeft: '4px solid transparent',
                            borderRight: '4px solid transparent',
                            borderTop: `4px solid ${effectiveIsLight ? '#1f2937' : '#f9fafb'}`
                          }}
                        />
                      </div>
                    </div>
                    
                  </button>
                </div>
              )}
            </div>

            {/* Chat Input Component - Bottom */}
            <ChatInput
              input={input}
              setInput={setInput}
              onSendMessage={sendMessage}
              configData={configData}
              loading={loading}
              pendingMsgCount={pendingMsgCount}
              allowSendMsg={allowSendMsg}
              messages={activeMessages}
              activeChatId={activeChatId}
              isSidebarOpen={isSidebarOpen}
              themeOverride={themeOverride}
            />
          </>
        )}
      </div>

      {/* Reference Modal Component */}
      <ReferenceModal
        isModalOpen={isModalOpen}
        onClose={closeModal}
        isLoadingChunks={isLoadingChunks}
        reference={reference}
        configData={configData}
        onToggleChunk={toggleChunk}
      />
    </div>
  );
};

export default Chat;